import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Items.css';
import { api } from '../services/db';
import LoadingOverlay from '../components/LoadingOverlay';
import { sortReportItems } from '../utils/helpers';

export default function Items() {
  const { warehouseId, teamRoles } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check Permissions
    if (teamRoles.length > 0 && !teamRoles.includes('admin') && !teamRoles.includes('owner')) {
      navigate('/home');
      return;
    }

    if (warehouseId) {
      loadItems();
    }
  }, [warehouseId, teamRoles, navigate]);

  async function loadItems() {
    setLoading(true);
    const data = await api.getItems(warehouseId);
    const sorted = sortReportItems(data);
    setItems(sorted);
    setLoading(false);
  }

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [currentItem, setCurrentItem] = useState(null);
  const [itemNameInput, setItemNameInput] = useState('');
  const [itemStockInput, setItemStockInput] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [itemToDelete, setItemToDelete] = useState(null);

  const [isProcessing, setIsProcessing] = useState(false);

  // Open "Add Item" Modal
  function openAdd() {
    setModalMode('add');
    setItemNameInput('');
    setShowModal(true);
  }

  // Open "Edit Item" Modal
  function openEdit(item) {
    setModalMode('edit');
    setCurrentItem(item);
    setItemNameInput(item.name);
    setItemStockInput(item.current_stock);
    setShowModal(true);
  }

  // Handle Save (Add or Edit)
  async function handleSave() {
    const trimmedName = itemNameInput.trim();
    if (!trimmedName) return;

    // Normalize name for comparison: lowercase, remove ALL spaces
    const normalizedInput = trimmedName.toLowerCase().replace(/\s+/g, '');

    // Check for duplicates in the same warehouse
    const duplicate = items.find(item => {
      // When editing, exclude the current item from duplicate check
      if (modalMode === 'edit' && item.$id === currentItem.$id) {
        return false;
      }
      const normalizedExisting = item.name.trim().toLowerCase().replace(/\s+/g, '');
      return normalizedExisting === normalizedInput;
    });

    if (duplicate) {
      alert(`الصنف "${trimmedName}" موجود بالفعل في هذا المستودع`);
      return;
    }

    setIsProcessing(true);
    try {
      if (modalMode === 'add') {
        await api.addItem({
          name: trimmedName,
          current_stock: 0,
          category: 'عام'
        }, warehouseId);
      } else {
        // Edit logic relies on updateItem in db.js
        await api.updateItem(currentItem.$id, { name: trimmedName, current_stock: Number(itemStockInput) || 0 }, warehouseId);
      }
      setShowModal(false);
      loadItems();
    } catch (err) {
      console.error(err);
      alert("فشل الحفظ");
    } finally {
      setIsProcessing(false);
    }
  }

  // Open "Delete Confirm" Modal
  function handleDeleteClick(item) {
    setItemToDelete(item);
    setShowDeleteModal(true);
  }

  // Execute Delete
  async function confirmDelete() {
    if (!itemToDelete) return;
    setIsProcessing(true);
    try {
      await api.deleteItem(itemToDelete.$id, warehouseId);
      setItems(items.filter(i => i.$id !== itemToDelete.$id));
      setShowDeleteModal(false);
    } catch (err) {
      console.error("Delete failed", err);
    } finally {
      setIsProcessing(false);
    }
  }

  function cancelModal() {
    setShowModal(false);
    setShowDeleteModal(false);
  }

  return (
    <div className="items-root" dir="rtl">
      <LoadingOverlay isLoading={isProcessing || loading} />
      <Link to="/home"><button id="back-btn">رجوع</button></Link>
      {/* <Link to="/home"><button id="back-btn">رجوع</button></Link> */}
      <div id="add-btn-holder"><button id="add-item" onClick={openAdd}>اضافة صنف</button></div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="item-modal-overlay">
          <div className="item-modal-content">
            <h3>{modalMode === 'add' ? 'إضافة صنف جديد' : 'تعديل الصنف'}</h3>
            <input
              type="text"
              placeholder="اسم الصنف"
              value={itemNameInput}
              onChange={e => setItemNameInput(e.target.value)}
            />
            {modalMode === 'edit' && (
              <input
                type="number"
                placeholder="الرصيد الحالي"
                value={itemStockInput}
                onChange={e => setItemStockInput(e.target.value)}
                style={{ marginTop: '10px' }}
              />
            )}
            <div className="item-modal-actions">
              <button className="item-modal-btn btn-save" onClick={handleSave}>حفظ</button>
              <button className="item-modal-btn btn-cancel" onClick={cancelModal}>إلغاء</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="item-modal-overlay">
          <div className="item-modal-content">
            <h3>هل أنت متأكد من الحذف؟</h3>
            <p>لا يمكن التراجع عن هذا الإجراء.</p>
            <div className="item-modal-actions">
              <button className="item-modal-btn btn-confirm-delete" onClick={confirmDelete}>حذف</button>
              <button className="item-modal-btn btn-cancel" onClick={cancelModal}>إلغاء</button>
            </div>
          </div>
        </div>
      )}
      {loading ? <p>جاري التحميل...</p> : (
        <table>
          <thead>
            <tr>
              <th style={{ width: '20pc' }}>الاصناف</th>
              <th>الرصيد الحالي</th>
              <th id='butcol'>الإجراء</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.$id}>
                <td>{it.name}</td>
                <td>{it.current_stock}</td>
                <td>
                  <button id="delete-butt" onClick={() => handleDeleteClick(it)}>حذف</button>
                  <button id="edit" onClick={() => openEdit(it)}>تعديل</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
